type StoredValue<T> = {
  value: T
  timestamp: number
}

type StorageLike<T> = {
  getItem: (key: string, initialValue: T) => T
  setItem: (key: string, value: T) => void
  removeItem: (key: string) => void
}

export function createExpiringStorage<T>(
  maxAgeMs: number,
  keyPrefix = ''
): StorageLike<T> {
  return {
    getItem: (key, initialValue) => {
      if (typeof window === 'undefined') {
        return initialValue
      }
      const raw = window.localStorage.getItem(`${keyPrefix}${key}`)
      if (!raw) return initialValue

      try {
        const parsed = JSON.parse(raw) as StoredValue<T>
        if (parsed && typeof parsed === 'object' && 'timestamp' in parsed) {
          if (Date.now() - parsed.timestamp > maxAgeMs) {
            window.localStorage.removeItem(`${keyPrefix}${key}`)
            return initialValue
          }
          return parsed.value ?? initialValue
        }

        // Backward compatibility: return unwrapped value
        return parsed as unknown as T
      } catch {
        return initialValue
      }
    },
    setItem: (key, value) => {
      if (typeof window === 'undefined') return
      const payload: StoredValue<T> = { value, timestamp: Date.now() }
      window.localStorage.setItem(`${keyPrefix}${key}`, JSON.stringify(payload))
    },
    removeItem: key => {
      if (typeof window === 'undefined') return
      window.localStorage.removeItem(`${keyPrefix}${key}`)
    }
  }
}
